import React from "react";
import { Columns, RotateCcw, ChevronUp, ChevronDown } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";

/**
 * "Columns" button that opens a dropdown of rows.
 * Each row has:
 *   - a checkbox (show/hide),
 *   - up / down arrows to reorder columns (locked ones can't be moved).
 *
 * Props:
 *  - columns         [{ key, label }]           full column list (canonical order)
 *  - order           string[]                    ordered keys currently applied
 *  - hidden          Set<string>                 currently hidden keys
 *  - onOrderChange   (nextOrder) =>              save new order
 *  - onHiddenChange  (nextHiddenSet) =>          save new hidden set
 *  - onReset         () =>                       restore defaults (all visible, canonical order)
 *  - lockedKeys      string[]                    columns that can't be hidden / moved
 *  - buttonSize      "sm" | "xs"
 *  - testId
 */
export default function ColumnPicker({
  columns,
  order,
  hidden,
  onOrderChange,
  onHiddenChange,
  onReset,
  lockedKeys = [],
  buttonSize = "sm",
  testId = "column-picker",
}) {
  // Build the display sequence: locked keys first (preserving their order), then ordered.
  const byKey = React.useMemo(
    () => Object.fromEntries(columns.map((c) => [c.key, c])),
    [columns]
  );
  const effectiveOrder = React.useMemo(() => {
    const set = new Set(order);
    const missing = columns.map((c) => c.key).filter((k) => !set.has(k));
    return [...order, ...missing];
  }, [order, columns]);

  const move = (key, dir) => {
    if (lockedKeys.includes(key)) return;
    const idx = effectiveOrder.indexOf(key);
    if (idx < 0) return;
    const swapIdx = dir === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= effectiveOrder.length) return;
    // Don't allow swapping with a locked column above/below
    if (lockedKeys.includes(effectiveOrder[swapIdx])) return;
    const next = [...effectiveOrder];
    [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
    onOrderChange(next);
  };

  const toggle = (key) => {
    if (lockedKeys.includes(key)) return;
    const next = new Set(hidden);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onHiddenChange(next);
  };

  const visibleCount = columns.length - hidden.size;
  const btnCls = buttonSize === "xs"
    ? "sharp h-6 px-2 text-[11px] bg-white border-zinc-400"
    : "sharp h-9 px-3 border-zinc-300";

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={btnCls} data-testid={testId}>
          <Columns className="h-3.5 w-3.5 mr-1.5" />
          Columns
          <span className="ml-1.5 font-mono text-[10px] text-zinc-500">
            {visibleCount}/{columns.length}
          </span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="sharp w-72 max-h-[70vh] overflow-y-auto p-1"
        data-testid={`${testId}-menu`}
      >
        <DropdownMenuLabel className="flex items-center justify-between text-[11px] uppercase tracking-wider text-zinc-500">
          <span>Show &amp; reorder columns</span>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <div className="py-1">
          {effectiveOrder.map((key, i) => {
            const col = byKey[key];
            if (!col) return null;
            const locked = lockedKeys.includes(key);
            const checked = !hidden.has(key);
            const canUp = i > 0 && !locked && !lockedKeys.includes(effectiveOrder[i - 1]);
            const canDown = i < effectiveOrder.length - 1 && !locked && !lockedKeys.includes(effectiveOrder[i + 1]);
            return (
              <div
                key={key}
                className="flex items-center gap-2 px-2 py-1 text-[12px] hover:bg-zinc-50"
                data-testid={`col-row-${key}`}
              >
                <Checkbox
                  checked={checked}
                  disabled={locked}
                  onCheckedChange={() => toggle(key)}
                  className="h-3.5 w-3.5 rounded-none"
                  data-testid={`col-toggle-${key}`}
                />
                <span className="flex-1 truncate select-none">{col.label}</span>
                {locked && <span className="text-[9px] text-zinc-400 tracking-wider">LOCKED</span>}
                {!locked && (
                  <>
                    <button
                      type="button"
                      disabled={!canUp}
                      onClick={() => move(key, "up")}
                      className="h-5 w-5 flex items-center justify-center border border-zinc-300 bg-white text-zinc-700 disabled:opacity-30 hover:bg-zinc-100"
                      title="Move up"
                      data-testid={`col-up-${key}`}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      type="button"
                      disabled={!canDown}
                      onClick={() => move(key, "down")}
                      className="h-5 w-5 flex items-center justify-center border border-zinc-300 bg-white text-zinc-700 disabled:opacity-30 hover:bg-zinc-100"
                      title="Move down"
                      data-testid={`col-down-${key}`}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>

        <DropdownMenuSeparator />
        <DropdownMenuItem
          onSelect={(e) => { e.preventDefault(); onReset(); }}
          className="text-[12px] font-medium text-[#0056B3]"
          data-testid={`${testId}-reset`}
        >
          <RotateCcw className="h-3.5 w-3.5 mr-2" /> Restore defaults
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/**
 * Hook that manages column visibility + order together, persisted to localStorage.
 *
 * Returns { hidden, order, setHidden, setOrder, reset, visibleColumns }.
 * `visibleColumns` = the input `columns` filtered and sorted by the current state.
 */
export function useColumnPrefs(storageKey, columns, defaults = { hidden: [], order: null }) {
  const canonicalOrder = React.useMemo(() => columns.map((c) => c.key), [columns]);

  const [state, setState] = React.useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw);
        return {
          hidden: new Set(parsed.hidden || defaults.hidden),
          order: Array.isArray(parsed.order) && parsed.order.length ? parsed.order : canonicalOrder,
        };
      }
    } catch { /* ignore */ }
    return {
      hidden: new Set(defaults.hidden),
      order: defaults.order || canonicalOrder,
    };
  });

  const persist = React.useCallback((next) => {
    setState(next);
    try {
      window.localStorage.setItem(storageKey, JSON.stringify({
        hidden: Array.from(next.hidden),
        order: next.order,
      }));
    } catch { /* ignore */ }
  }, [storageKey]);

  const setHidden = React.useCallback((nextHidden) => {
    persist({ ...state, hidden: nextHidden });
  }, [persist, state]);

  const setOrder = React.useCallback((nextOrder) => {
    persist({ ...state, order: nextOrder });
  }, [persist, state]);

  const reset = React.useCallback(() => {
    const next = { hidden: new Set(), order: canonicalOrder };
    setState(next);
    try { window.localStorage.removeItem(storageKey); } catch { /* ignore */ }
  }, [storageKey, canonicalOrder]);

  const visibleColumns = React.useMemo(() => {
    const byKey = Object.fromEntries(columns.map((c) => [c.key, c]));
    const seen = new Set();
    const list = [];
    // include ordered keys first
    for (const k of state.order) {
      if (byKey[k] && !seen.has(k)) {
        seen.add(k);
        if (!state.hidden.has(k)) list.push(byKey[k]);
      }
    }
    // append any missing (new) keys
    for (const c of columns) {
      if (!seen.has(c.key) && !state.hidden.has(c.key)) list.push(c);
    }
    return list;
  }, [columns, state]);

  return {
    hidden: state.hidden,
    order: state.order,
    setHidden,
    setOrder,
    reset,
    visibleColumns,
  };
}

// Backwards-compatible hook (visibility only) kept so any older imports keep working.
export function useHiddenColumns(storageKey, defaults = []) {
  const [hidden, setHiddenState] = React.useState(() => {
    try {
      const raw = window.localStorage.getItem(storageKey);
      if (raw) return new Set(JSON.parse(raw));
    } catch { /* ignore */ }
    return new Set(defaults);
  });
  const setHidden = React.useCallback((next) => {
    setHiddenState(next);
    try { window.localStorage.setItem(storageKey, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
  }, [storageKey]);
  const reset = React.useCallback(() => {
    setHiddenState(new Set());
    try { window.localStorage.removeItem(storageKey); } catch { /* ignore */ }
  }, [storageKey]);
  return [hidden, setHidden, reset];
}
